import { callApi } from '@/store/apiUtils'
import type { TGiftTier, TGift, TRedeemableGift } from '@/store/gift/state'
import { giftModule } from '@/store/gift/module'
import { router } from '@/router'
import { fetchLoadable } from '@/store/utils'
import * as Sentry from '@sentry/browser'
import { getInitialGiftState } from '@/store/gift/state'
import { analyticsModule } from '@/store/analytics/module'
import { userModule } from '@/store/user/module'

const createGiftCheckoutSession = async ({ tier }: { tier: TGiftTier }) => {
    const data: {
        tier: TGiftTier
        amplitudeDeviceId?: string
        amplitudeUserId?: string
    } = { tier }

    const amplitudeDeviceId = analyticsModule.actions.getAmplitudeDeviceId()
    const amplitudeUserId = analyticsModule.actions.getAmplitudeUserId()
    if (amplitudeDeviceId) {
        data.amplitudeDeviceId = amplitudeDeviceId
    }

    if (amplitudeUserId) {
        data.amplitudeUserId = amplitudeUserId
    }

    const response: { clientSecret: string | null; checkoutSessionId: string } = await callApi({
        method: 'POST',
        url: '/billing/gifts/checkout',
        data,
    })
    return response
}

/**
 * Uses a completed checkout session to create a gift.
 * If gift was already created, will return the previous gift.
 */
const createGift = async ({ checkoutSessionId }: { checkoutSessionId: string }) => {
    // `wasCreated` distinguishes the first call from a refresh; the caller only
    // needs the gift, which is the same either way.
    const { gift }: { gift: TGift; wasCreated: boolean } = await callApi({
        method: 'POST',
        url: '/billing/gifts',
        data: { checkoutSessionId },
    })
    return gift
}

const updateGift = async ({ giftId, checkoutSessionId, invitedEmail }: {
    giftId: string
    checkoutSessionId: string
    invitedEmail: string
}) => {
    const { gift }: { gift: TGift } = await callApi({
        method: 'PATCH',
        url: `/billing/gifts/${giftId}`,
        data: { checkoutSessionId, invitedEmail },
    })
    return gift
}

const validateGift = async () => {
    // grab the gift query from the url rather than each component having to pass it
    const redemptionToken = router.currentRoute.value.query?.redemptionToken

    if (!redemptionToken || typeof redemptionToken !== 'string') {
        return null
    }

    return fetchLoadable(giftModule.state.validGift, async () => {
        const response: { gift: TRedeemableGift } | null = await callApi({
            method: 'POST',
            url: '/billing/gifts/validate',
            data: { redemptionToken },
        }).catch(() => {
            Sentry.captureException(new Error(`Activate Gift error: ${redemptionToken}`))
            giftModule.state.giftError =
                'Invalid gift link. Try again – if the problem persists, contact support.'
        })

        const gift = response?.gift
        if (!gift) {
            return null
        }

        if (!gift.isRedeemable) {
            giftModule.state.giftError = 'Invalid gift link. Try again – if the problem persists, contact support.'
            return null
        }

        giftModule.state.redemptionToken = redemptionToken

        analyticsModule.actions.amplitudeTrack('Gift Redemption Started', {
            giftId: gift.giftId,
            giftTier: gift.tier,
        })

        return gift
    })
}

const activateGift = async () => {
    const redemptionToken = router.currentRoute.value.query?.redemptionToken

    if (!redemptionToken || typeof redemptionToken !== 'string') {
        throw new Error('Activate Gift error: missing redemptionToken')
    }

    const user = userModule.state.user
    if (!user) {
        throw new Error('Activate Gift error: no current user')
    }

    const validGift = await validateGift()
    if (!validGift) {
        throw new Error(`Activate Gift error: gift not validated: ${redemptionToken}`)
    }

    const activateGiftResponse = await callApi({
        method: 'POST',
        url: '/billing/subscriptions/activate/gift',
        data: { redemptionToken, giftId: validGift.giftId },
    }).catch((err: unknown) => {
        Sentry.captureException(new Error(`Activate Gift error: ${redemptionToken}`))
        throw err
    })

    if (!activateGiftResponse) {
        Sentry.captureException(new Error(`No activate gift response: ${redemptionToken}`))
        throw new Error('Gift Error: Unable to redeem gift.')
    }

    await userModule.actions.fetchUserData({ userData: true })
    analyticsModule.actions.updateIntercom()
    analyticsModule.actions.amplitudeTrack(
        'Gift Redeemed', {
            email: user.email,
            giftId: validGift.giftId,
            giftTier: validGift.tier,
        })
}

const wipeGiftContext = async () => {
    const initial = getInitialGiftState()
    giftModule.state.validGift = initial.validGift
    giftModule.state.redemptionToken = initial.redemptionToken
    giftModule.state.giftError = initial.giftError
    giftModule.state.hasSeenRedeemGiftNotification = true
    giftModule.state.isShowingRedeemGiftModal = false

    // Remove the gift redemptionToken query param from the route, if present
    const currentRoute = router.currentRoute.value
    if (currentRoute.name && currentRoute.query?.redemptionToken) {
        await router.replace({
            name: currentRoute.name,
            params: {
                ...currentRoute.params,
            },
            query: {
                ...currentRoute.query,
                redemptionToken: undefined,
            },
        })
    }
}

const openRedeemGiftModal = () => {
    giftModule.state.isShowingRedeemGiftModal = true
}

const closeRedeemGiftModal = async () => {
    giftModule.state.isShowingRedeemGiftModal = false

    await wipeGiftContext()
}

const dismissRedeemGiftModal = () => {
    giftModule.state.isShowingRedeemGiftModal = false
    giftModule.state.hasSeenRedeemGiftNotification = true
}

const trackHasSeenRedeemGiftNotification = () => {
    giftModule.state.hasSeenRedeemGiftNotification = true
}

export default {
    createGiftCheckoutSession,
    createGift,
    updateGift,
    validateGift,
    activateGift,
    wipeGiftContext,
    openRedeemGiftModal,
    closeRedeemGiftModal,
    dismissRedeemGiftModal,
    trackHasSeenRedeemGiftNotification,
}
